import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Swords, MailCheck } from "lucide-react";
import api, { apiErr } from "@/lib/api";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-zinc-950">
      <div className="w-full max-w-sm fade-up">
        <div className="flex items-center gap-3 mb-8">
          <Swords className="text-amber-500" size={32} />
          <span className="font-cinzel text-2xl text-amber-300 uppercase tracking-widest">Ludus</span>
        </div>

        {sent ? (
          <div data-testid="forgot-sent-message">
            <div className="flex items-center gap-2 text-amber-400 mb-3">
              <MailCheck size={22} />
              <h2 className="font-cinzel text-2xl text-zinc-100">Check your scrolls</h2>
            </div>
            <p className="text-zinc-400 text-sm leading-relaxed mb-6">
              If an account exists for <span className="text-amber-300">{email}</span>, a password setup or reset link has been dispatched. The link expires in 1 hour.
            </p>
            <button data-testid="back-to-login-btn" onClick={() => navigate("/login")}
              className="w-full bg-amber-900/30 text-amber-300 border border-amber-700/60 px-5 py-3 uppercase tracking-[0.2em] font-cinzel hover:bg-amber-900/50 hover:border-amber-500 transition-all">
              Return to Login
            </button>
          </div>
        ) : (
          <>
            <h2 className="font-cinzel text-2xl text-zinc-100 mb-1">Reset your password</h2>
            <p className="text-zinc-500 text-sm mb-6">Enter your email to reset your password, or set one if you previously signed in with Google.</p>
            <form onSubmit={submit} className="space-y-4">
              <input data-testid="forgot-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="Email"
                className="w-full bg-zinc-900 border border-amber-900/40 px-4 py-3 text-zinc-100 placeholder-zinc-600 focus:border-amber-600 outline-none rounded-sm" />
              <button data-testid="forgot-submit-btn" disabled={busy} type="submit"
                className="w-full bg-amber-900/30 text-amber-300 border border-amber-700/60 px-5 py-3 uppercase tracking-[0.2em] font-cinzel hover:bg-amber-900/50 hover:border-amber-500 transition-all disabled:opacity-50">
                {busy ? "…" : "Send Reset Link"}
              </button>
            </form>
            <p className="text-center text-sm text-zinc-500 mt-6">
              Remembered it?{" "}
              <button data-testid="forgot-back-link" onClick={() => navigate("/login")} className="text-amber-400 hover:text-amber-300 underline">
                Sign in
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
